/**
 * This slice is to manage pick lists.
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
    assetService,
    contentService,
    itemService,
    linkService,
    procedureService,
    zoneService,
} from 'features/services';
import { LoadingStatus } from 'GeminiViewerComponent/helpers/AsyncStatus';

const initialState = {
    assetsPicklist: {
        status: LoadingStatus.Idle,
        errorMessage: '',
        assets: [],
    },
    zonesPicklist: {
        status: LoadingStatus.Idle,
        errorMessage: '',
        zones: [],
    },
    itemsPicklist: {
        status: LoadingStatus.Idle,
        errorMessage: '',
        items: [],
    },
    associatedViewAssetsPicklist: {
        status: LoadingStatus.Idle,
        errorMessage: '',
        assets: [],
    },
    associatedViewZonesPicklist: {
        status: LoadingStatus.Idle,
        errorMessage: '',
        zones: [],
    },
    associatedViewItemsPicklist: {
        status: LoadingStatus.Idle,
        errorMessage: '',
        items: [],
    },
    linksPicklist: {
        status: LoadingStatus.Idle,
        errorMessage: '',
        links: [],
    },
    contentPicklist: {
        status: LoadingStatus.Idle,
        errorMessage: '',
        content: [],
    },
    proceduresPicklist: {
        status: LoadingStatus.Idle,
        errorMessage: '',
        procedures: [],
    },
    clientAssetsPicklist: {
        status: LoadingStatus.Idle,
        errorMessage: '',
        clientAssets: [],
    },
};

export const getAssetsPicklist = createAsyncThunk('picklistsSlice/getAssetsList', async () => {
    return await assetService.getAssetsList();
});

export const getAssociatedViewAssetsPicklist = createAsyncThunk(
    'picklistsSlice/getAssociatedViewAssetsPicklist',
    async () => {
        return await assetService.getAssetsList();
    }
);

export const getClientAssetsPicklist = createAsyncThunk('picklistsSlice/getClientAssetsList', async (clientId) => {
    return await assetService.getClientAssetsList(clientId);
});

export const getZonesPicklist = createAsyncThunk(`picklistsSlice/getZonesPicklist`, async (params, { getState }) => {
    const response = await zoneService.getAll(
        params.assetId,
        false,
        params.includeChildren,
        getState().accounts.activeUser.s3_prefix,
        true
    );
    return response;
});

export const getAssociatedViewZonesPicklist = createAsyncThunk(
    `picklistsSlice/getAssociatedViewZonesPicklist`,
    async (params, { getState }) => {
        const response = await zoneService.getAll(
            params.assetId,
            false,
            params.includeChildren,
            getState().accounts.activeUser.s3_prefix,
            true
        );
        return response;
    }
);

export const getItemsPicklist = createAsyncThunk(`picklistsSlice/getItemsPicklist`, async (params, { getState }) => {
    const response = await itemService.getAll(params.zoneId, getState().accounts.activeUser.s3_prefix);
    return response;
});

export const getAssociatedViewItemsPicklist = createAsyncThunk(
    `picklistsSlice/getAssociatedViewItemsPicklist`,
    async (params, { getState }) => {
        const response = await itemService.getAll(params.zoneId, getState().accounts.activeUser.s3_prefix);
        return response;
    }
);

export const getLinksPicklist = createAsyncThunk('picklistsSlice/getLinksPicklist', async (params, { getState }) => {
    return await linkService.getAllItemLinksRange(
        0,
        0,
        params.itemId,
        params.linkTypeId,
        getState().accounts.activeUser.s3_prefix
    );
});

export const getContentPicklist = createAsyncThunk(
    'picklistsSlice/fetchContentByTypeId',
    async (params, { getState }) =>
        await contentService.getAllContentByTypeIdRange(
            0,
            0,
            params.contentTypeIds,
            params.asset_id ?? null,
            params.searchString,
            params.sort ?? 'display_name',
            getState().accounts.activeUser.s3_prefix
        )
);

export const getProceduresPicklist = createAsyncThunk('picklistsSlice/getProceduresPicklist', async () => {
    return await procedureService.getProceduresList();
});

const picklistsSlice = createSlice({
    name: 'picklists',
    initialState,
    reducers: {
        resetAssetsPicklistStatus: (state) => {
            state.assetsPicklist.status = LoadingStatus.Idle;
            state.assetsPicklist.assets = [];
            state.assetsPicklist.errorMessage = '';
        },
        resetZonesPicklistStatus: (state) => {
            state.zonesPicklist.status = LoadingStatus.Idle;
            state.zonesPicklist.zones = [];
            state.zonesPicklist.errorMessage = '';
        },
        resetItemsPicklistStatus: (state) => {
            state.itemsPicklist.status = LoadingStatus.Idle;
            state.itemsPicklist.items = [];
            state.itemsPicklist.errorMessage = '';
        },
        resetAssociatedViewAssetsPicklistStatus: (state) => {
            state.associatedViewAssetsPicklist.status = LoadingStatus.Idle;
            state.associatedViewAssetsPicklist.assets = [];
            state.associatedViewAssetsPicklist.errorMessage = '';
        },
        resetAssociatedViewZonesPicklistStatus: (state) => {
            state.associatedViewZonesPicklist.status = LoadingStatus.Idle;
            state.associatedViewZonesPicklist.zones = [];
            state.associatedViewZonesPicklist.errorMessage = '';
        },
        resetAssociatedViewItemsPicklistStatus: (state) => {
            state.associatedViewItemsPicklist.status = LoadingStatus.Idle;
            state.associatedViewItemsPicklist.items = [];
            state.associatedViewItemsPicklist.errorMessage = '';
        },
        resetLinksPicklistStatus: (state) => {
            state.linksPicklist.status = LoadingStatus.Idle;
            state.linksPicklist.links = [];
            state.linksPicklist.errorMessage = '';
        },
        resetContentPicklistStatus: (state) => {
            state.contentPicklist.status = LoadingStatus.Idle;
            state.contentPicklist.content = [];
            state.contentPicklist.errorMessage = '';
        },
        resetProceduresPicklistStatus: (state) => {
            state.proceduresPicklist.status = LoadingStatus.Idle;
            state.proceduresPicklist.procedures = [];
            state.proceduresPicklist.errorMessage = '';
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getAssetsPicklist.pending, (state, action) => {
                state.assetsPicklist.status = LoadingStatus.Pending;
            })
            .addCase(getAssetsPicklist.fulfilled, (state, action) => {
                state.assetsPicklist.assets = action.payload;
                state.assetsPicklist.status = LoadingStatus.Loaded;
            })
            .addCase(getAssetsPicklist.rejected, (state, action) => {
                state.assetsPicklist.status = LoadingStatus.Failed;
                state.assetsPicklist.errorMessage = action.error.message;
            })
            .addCase(getAssociatedViewAssetsPicklist.pending, (state, action) => {
                state.associatedViewAssetsPicklist.status = LoadingStatus.Pending;
            })
            .addCase(getAssociatedViewAssetsPicklist.fulfilled, (state, action) => {
                state.associatedViewAssetsPicklist.assets = action.payload;
                state.assetsPicklist.assets = action.payload;
                state.associatedViewAssetsPicklist.status = LoadingStatus.Loaded;
            })
            .addCase(getAssociatedViewAssetsPicklist.rejected, (state, action) => {
                state.associatedViewAssetsPicklist.status = LoadingStatus.Failed;
                state.associatedViewAssetsPicklist.errorMessage = action.error.message;
            })
            .addCase(getClientAssetsPicklist.pending, (state, action) => {
                state.clientAssetsPicklist.status = LoadingStatus.Pending;
            })
            .addCase(getClientAssetsPicklist.fulfilled, (state, action) => {
                state.clientAssetsPicklist.clientAssets = action.payload;
                state.clientAssetsPicklist.status = LoadingStatus.Loaded;
            })
            .addCase(getClientAssetsPicklist.rejected, (state, action) => {
                state.clientAssetsPicklist.clientAssets = [];
                state.clientAssetsPicklist.status = LoadingStatus.Failed;
                state.clientAssetsPicklist.errorMessage = action.error.message;
            })
            .addCase(getZonesPicklist.pending, (state, action) => {
                state.zonesPicklist.status = LoadingStatus.Pending;
            })
            .addCase(getZonesPicklist.fulfilled, (state, action) => {
                state.zonesPicklist.zones = action.payload.zones;
                state.zonesPicklist.status = LoadingStatus.Loaded;
            })
            .addCase(getZonesPicklist.rejected, (state, action) => {
                state.zonesPicklist.status = LoadingStatus.Failed;
                state.zonesPicklist.errorMessage = action.error.message;
            })
            .addCase(getAssociatedViewZonesPicklist.pending, (state, action) => {
                state.associatedViewZonesPicklist.status = LoadingStatus.Pending;
            })
            .addCase(getAssociatedViewZonesPicklist.fulfilled, (state, action) => {
                state.associatedViewZonesPicklist.zones = action.payload.zones;
                state.associatedViewZonesPicklist.status = LoadingStatus.Loaded;
            })
            .addCase(getAssociatedViewZonesPicklist.rejected, (state, action) => {
                state.associatedViewZonesPicklist.status = LoadingStatus.Failed;
                state.associatedViewZonesPicklist.errorMessage = action.error.message;
            })
            .addCase(getItemsPicklist.pending, (state, action) => {
                state.itemsPicklist.status = LoadingStatus.Pending;
            })
            .addCase(getItemsPicklist.fulfilled, (state, action) => {
                state.itemsPicklist.items = action.payload.items;
                state.itemsPicklist.status = LoadingStatus.Loaded;
            })
            .addCase(getItemsPicklist.rejected, (state, action) => {
                state.itemsPicklist.status = LoadingStatus.Failed;
                state.itemsPicklist.errorMessage = action.error.message;
            })
            .addCase(getAssociatedViewItemsPicklist.pending, (state, action) => {
                state.associatedViewItemsPicklist.status = LoadingStatus.Pending;
            })
            .addCase(getAssociatedViewItemsPicklist.fulfilled, (state, action) => {
                state.associatedViewItemsPicklist.items = action.payload.items;
                state.associatedViewItemsPicklist.status = LoadingStatus.Loaded;
            })
            .addCase(getAssociatedViewItemsPicklist.rejected, (state, action) => {
                state.associatedViewItemsPicklist.status = LoadingStatus.Failed;
                state.associatedViewItemsPicklist.errorMessage = action.error.message;
            })
            .addCase(getLinksPicklist.pending, (state, action) => {
                state.linksPicklist.status = LoadingStatus.Pending;
            })
            .addCase(getLinksPicklist.fulfilled, (state, action) => {
                state.linksPicklist.links = action.payload.itemLinks;
                state.linksPicklist.status = LoadingStatus.Loaded;
            })
            .addCase(getLinksPicklist.rejected, (state, action) => {
                state.linksPicklist.status = LoadingStatus.Failed;
                state.linksPicklist.errorMessage = action.error.message;
            })
            .addCase(getContentPicklist.pending, (state, action) => {
                state.contentPicklist.status = LoadingStatus.Pending;
            })
            .addCase(getContentPicklist.fulfilled, (state, action) => {
                state.contentPicklist.content = action.payload;
                state.contentPicklist.status = LoadingStatus.Loaded;
            })
            .addCase(getContentPicklist.rejected, (state, action) => {
                state.contentPicklist.status = LoadingStatus.Failed;
                state.contentPicklist.errorMessage = action.error.message;
            })
            .addCase(getProceduresPicklist.pending, (state, action) => {
                state.proceduresPicklist.status = LoadingStatus.Pending;
            })
            .addCase(getProceduresPicklist.fulfilled, (state, action) => {
                state.proceduresPicklist.procedures = action.payload;
                state.proceduresPicklist.status = LoadingStatus.Loaded;
            })
            .addCase(getProceduresPicklist.rejected, (state, action) => {
                state.proceduresPicklist.status = LoadingStatus.Failed;
                state.proceduresPicklist.errorMessage = action.error.message;
            });
    },
});

export const selectAssetsPicklistLoadingStatus = (state) => state.picklists.assetsPicklist.status;
export const selectAssetsPicklist = (state) => state.picklists.assetsPicklist.assets;
export const selectAssociatedViewAssetsPicklistLoadingStatus = (state) =>
    state.picklists.associatedViewAssetsPicklist.status;
export const selectAssociatedViewAssetsPicklist = (state) => state.picklists.associatedViewAssetsPicklist.assets;

export const selectZonesPicklistLoadingStatus = (state) => state.picklists.zonesPicklist.status;
export const selectZonesPicklist = (state) => state.picklists.zonesPicklist.zones;
export const selectAssociatedViewZonesPicklistLoadingStatus = (state) =>
    state.picklists.associatedViewZonesPicklist.status;
export const selectAssociatedViewZonesPicklist = (state) => state.picklists.associatedViewZonesPicklist.zones;

export const selectItemsPicklistLoadingStatus = (state) => state.picklists.itemsPicklist.status;
export const selectItemsPicklist = (state) => state.picklists.itemsPicklist.items;
export const selectAssociatedViewItemsPicklistLoadingStatus = (state) =>
    state.picklists.associatedViewItemsPicklist.status;
export const selectAssociatedViewItemsPicklist = (state) => state.picklists.associatedViewItemsPicklist.items;

export const selectLinksPicklistLoadingStatus = (state) => state.picklists.linksPicklist.status;
export const selectLinksPicklist = (state) => state.picklists.linksPicklist.links;

export const selectContentPicklistLoadingStatus = (state) => state.picklists.contentPicklist.status;
export const selectContentPicklist = (state) => state.picklists.contentPicklist.content;

export const selectProceduresPicklistLoadingStatus = (state) => state.picklists.proceduresPicklist.status;
export const selectProceduresPicklist = (state) => {
    return state.picklists.proceduresPicklist.procedures;
};

export const selectClientAssetsPicklistLoadingStatus = (state) => state.picklists.clientAssetsPicklist.status;
export const selectClientAssetsPicklist = (state) => state.picklists.clientAssetsPicklist.clientAssets;

export const {
    resetAssetsPicklistStatus,
    resetZonesPicklistStatus,
    resetItemsPicklistStatus,
    resetLinksPicklistStatus,
    resetContentPicklistStatus,
    resetProceduresPicklistStatus,
    resetAssociatedViewAssetsPicklistStatus,
    resetAssociatedViewItemsPicklistStatus,
    resetAssociatedViewZonesPicklistStatus,
} = picklistsSlice.actions;

export default picklistsSlice.reducer;
